import * as React from "react";
import {Accordion, Button, Checkbox, Divider, Dropdown, Form, Header, Input} from "semantic-ui-react";
import {
    EventSuppressionRules,
    Queue,
    QueueOptions,
    RateLimit,
    TransportOrderEventMirroringRules
} from "../../../../../generated/api";

import 'brace/mode/groovy'
import 'brace/ext/searchbox'
import {SuppressionRulesMask} from "./SuppressionRuleMask";

export interface QueueMaskProps {
    queueStr: string;
    onChange: (queue: Queue) => void;
}

export function QueueMask(props: QueueMaskProps) {
    const [queue, setQueue] = React.useState<Queue>({} as Queue);
    const [activeAccordions, setActiveAccordions] = React.useState<{
        options: boolean;
        rateLimits: boolean,
        suppressionRules: boolean,
        mirroring: boolean,
    }>({
        options: false,
        rateLimits: false,
        suppressionRules: false,
        mirroring: false,
    });
    const [suppressionAccordions, setSuppressionAccordions] = React.useState({
        eventTypes: false,
        transportOrderEvents: false
    });

    const [localRules, setLocalRules] = React.useState<EventSuppressionRules>(queue.suppressionRules || {});

    React.useEffect(() => {
        try {
            const parsedQueue: Queue = JSON.parse(props.queueStr);
            setQueue(parsedQueue);
            setLocalRules(parsedQueue.suppressionRules || {});
        } catch {
            setQueue({} as Queue);
            setLocalRules({});
        }
    }, [props.queueStr]);

    function handleChange<K extends keyof Queue>(key: K, value: Queue[K]) {
        const updated = {...queue, [key]: value};
        setQueue(updated);
        props.onChange(updated);
    }

    const typeOptions = Object.values(Queue.TypeEnum).map(v => ({
        key: v,
        text: v,
        value: v
    }));

    function renderOptionsMask(options?: QueueOptions) {
        const [localOptions, setLocalOptions] = React.useState<QueueOptions>(options || {});

        React.useEffect(() => {
            setLocalOptions(options || {});
        }, [options]);

        function update<K extends keyof QueueOptions>(key: K, value: QueueOptions[K]) {
            const updated = {...localOptions, [key]: value};
            setLocalOptions(updated);
            handleChange("options", updated);
        }

        return (
            <Form.Group widths="equal">
                <Form.Field>
                    <Checkbox
                        label="Include Forward Diff"
                        checked={!!localOptions.includeForwardDiff}
                        onChange={(_, d) => update("includeForwardDiff", d.checked)}
                    />
                </Form.Field>
                <Form.Field>
                    <label>Redeliver Unprocessed Events After Seconds</label>
                    <Input
                        type="number"
                        value={localOptions.redeliverUnprocessedEventsAfterSeconds ?? ""}
                        onChange={(_, d) => update("redeliverUnprocessedEventsAfterSeconds", d.value === "" ? undefined : Number(d.value))}
                        placeholder="Seconds"
                    />
                </Form.Field>
            </Form.Group>
        );
    }

    function renderRateLimitsMask(rateLimits?: RateLimit[]) {
        const [localLimits, setLocalLimits] = React.useState<RateLimit[]>(rateLimits || []);

        React.useEffect(() => {
            setLocalLimits(rateLimits || []);
        }, [rateLimits]);

        function updateLimit(index: number, key: keyof RateLimit, value: any) {
            const updated = localLimits.map((rl, i) =>
                i === index ? {...rl, [key]: value} : rl
            );
            setLocalLimits(updated);
            handleChange("rateLimits", updated);
        }

        function addLimit() {
            const updated = [...localLimits, {} as RateLimit];
            setLocalLimits(updated);
            handleChange("rateLimits", updated);
        }

        function removeLimit(index: number) {
            const updated = localLimits.filter((_, i) => i !== index);
            setLocalLimits(updated);
            handleChange("rateLimits", updated);
        }

        const eventTypeOptions = Object.values(RateLimit.EventTypeEnum).map(v => ({
            key: v,
            text: v,
            value: v
        }));

        return (
            <>
                {localLimits.map((rl, idx) => (
                    <Form.Group widths="equal" key={idx}>
                        <Form.Field>
                            <Checkbox
                                label="Enabled"
                                checked={!!rl.enabled}
                                onChange={(_, d) => updateLimit(idx, "enabled", d.checked)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Event Type</label>
                            <Dropdown
                                selection
                                options={eventTypeOptions}
                                value={rl.eventType || ""}
                                onChange={(_, d) => updateLimit(idx, "eventType", d.value as RateLimit.EventTypeEnum)}
                                placeholder="Event Type"
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Limit In Seconds</label>
                            <Input
                                type="number"
                                value={rl.limitInSeconds ?? ""}
                                onChange={(_, d) => updateLimit(idx, "limitInSeconds", d.value === "" ? undefined : Number(d.value))}
                                placeholder="Seconds"
                            />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox
                                label="Set Processed"
                                checked={!!rl.setProcessed}
                                onChange={(_, d) => updateLimit(idx, "setProcessed", d.checked)}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Button icon="trash" color="red" onClick={() => removeLimit(idx)}/>
                        </Form.Field>
                    </Form.Group>
                ))}
                <Button icon="plus" color="green" onClick={addLimit} content="Add"/>
            </>
        );
    }

    type MirroringRules = NonNullable<Queue["mirroring"]>;

    function MirroringMask(
        {value, onChange}: {
            value?: MirroringRules;
            onChange: (val: MirroringRules) => void;
        }) {

        const selected = value?.transportOrderEvents?.mirrorChangeTypes || [];
        const MIRROR_CHANGE_TYPES = Object.values(TransportOrderEventMirroringRules.MirrorChangeTypesEnum);

        function toggleType(type: TransportOrderEventMirroringRules.MirrorChangeTypesEnum) {
            const newSelected = selected.includes(type)
                ? selected.filter(t => t !== type)
                : [...selected, type];
            onChange({
                ...value,
                transportOrderEvents: {...value?.transportOrderEvents, mirrorChangeTypes: newSelected}
            });
        }

        return (
            <Accordion fluid styled>
                <Accordion.Title
                    active={activeAccordions.mirroring}
                    onClick={() => setActiveAccordions(a => ({...a, mirroring: !a.mirroring}))}
                    content="Event Mirroring"
                />
                <Accordion.Content active={activeAccordions.mirroring}>
                    <Form>
                        <Header as="h5">TransportOrderEvents</Header>
                        {MIRROR_CHANGE_TYPES.map(type => (
                            <Form.Field key={type}>
                                <Checkbox
                                    label={type}
                                    checked={selected.includes(type)}
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => toggleType(type)}
                                />
                            </Form.Field>
                        ))}
                        <Divider/>
                        <Header as="h5">TextMessageEvents</Header>
                        <Form.Field>
                            <Checkbox
                                label="TEXT_MESSAGE_EVENT"
                                checked={value?.mirrorTextMessageEvents !== false}   //  ticked unless explicitly false
                                onClick={e => e.stopPropagation()}
                                onChange={(_, d) => onChange({...value, mirrorTextMessageEvents: !!d.checked})}
                            />
                        </Form.Field>
                    </Form>
                </Accordion.Content>
            </Accordion>
        );
    }

    return (
        <Form>
            <Form.Field>
                <label>ID</label>
                <Input
                    value={queue._id || ""}
                    onChange={(_, d) => handleChange("_id", d.value)}
                    placeholder="ID"
                />
            </Form.Field>
            <Form.Field>
                <label>Company ID</label>
                <Input
                    value={queue.companyId || ""}
                    onChange={(_, d) => handleChange("companyId", d.value)}
                    placeholder="Company ID"
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Enabled"
                    checked={!!queue.enabled}
                    onChange={(_, d) => handleChange("enabled", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Enqueue Position Events"
                    checked={!!queue.enqueuePositionEvents}
                    onChange={(_, d) => handleChange("enqueuePositionEvents", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <Checkbox
                    label="Mirroring Enabled"
                    checked={!!queue.mirroringEnabled}
                    onChange={(_, d) => handleChange("mirroringEnabled", d.checked)}
                />
            </Form.Field>
            <Form.Field>
                <label>Name</label>
                <Input
                    value={queue.name || ""}
                    onChange={(_, d) => handleChange("name", d.value)}
                    placeholder="Name"
                />
            </Form.Field>
            <Form.Field>
                <label>Type</label>
                <Dropdown
                    selection
                    options={typeOptions}
                    value={queue.type || ""}
                    onChange={(_, d) => handleChange("type", d.value as Queue.TypeEnum)}
                    placeholder="Type"
                />
            </Form.Field>
            {/* Options Accordion */}
            <Accordion
                fluid
                styled
                panels={[
                    {
                        key: "options",
                        title: "Options",
                        content: {
                            content: renderOptionsMask(queue.options)
                        }
                    }
                ]}
                activeIndex={activeAccordions.options ? 0 : -1}
                onTitleClick={() =>
                    setActiveAccordions(a => ({...a, options: !a.options}))
                }
            />
            {/* RateLimits Accordion */}
            <Accordion
                fluid
                styled
                panels={[
                    {
                        key: "rateLimits",
                        title: `Rate Limits (${queue.rateLimits?.length ?? 0})`,
                        content: {
                            content: renderRateLimitsMask(queue.rateLimits)
                        }
                    }
                ]}
                activeIndex={activeAccordions.rateLimits ? 0 : -1}
                onTitleClick={() =>
                    setActiveAccordions(a => ({...a, rateLimits: !a.rateLimits}))
                }
            />
            <Accordion
                fluid
                styled
                panels={[
                    {
                        key: "suppressionRules",
                        title: "Suppression Rules",
                        content: {
                            content: (
                                <SuppressionRulesMask
                                    localRules={localRules}
                                    setLocalRules={setLocalRules}
                                    activeAccordions={suppressionAccordions}
                                    setActiveAccordions={setSuppressionAccordions}
                                    onChange={r => handleChange("suppressionRules", r)}
                                />
                            )
                        }
                    }
                ]}
                activeIndex={activeAccordions.suppressionRules ? 0 : -1}
                onTitleClick={() =>
                    setActiveAccordions(a => ({...a, suppressionRules: !a.suppressionRules}))
                }
            />
            <MirroringMask
                value={queue.mirroring}
                onChange={val => handleChange("mirroring", val)}
            />
        </Form>
    );
}